import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, MapPin, Heart, MessageCircle, Flag, Ban, ChevronLeft, ChevronRight, Sparkles, Shield, Crown, Gift, Lock, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import OnlineDot from './OnlineDot';
import GiftPickerModal from './GiftPickerModal';
import { getProfileById, reportUser, blockUser, requestPrivatePhotos } from '../services/api';
import { useDefaultAvatar } from '../services/defaultAvatar';
import { useAuth } from '../contexts/AuthContext';

/**
 * Full-profile modal. Uses existing GET /api/profiles/{id} — no new endpoints.
 * Reused wherever the app wants to show a full profile view.
 */
const ProfileDetailModal = ({ userId, onClose, onMessage, onLike, onReported, onBlocked }) => {
  const defaultAvatar = useDefaultAvatar();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [photoIndex, setPhotoIndex] = useState(0);
  const [showReport, setShowReport] = useState(false);
  const [reportReason, setReportReason] = useState('inappropriate');
  const [reportDetails, setReportDetails] = useState('');
  const [busy, setBusy] = useState(false);
  const [showGifts, setShowGifts] = useState(false);
  const [giftSent, setGiftSent] = useState(null);
  const [showPrivateRequest, setShowPrivateRequest] = useState(false);
  const [privateNote, setPrivateNote] = useState('');
  const [privateStatus, setPrivateStatus] = useState('');  // '', 'pending', 'sent', 'error'

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError('');
    getProfileById(userId)
      .then(res => { if (!cancelled) { setProfile(res.data); setPhotoIndex(res.data.primary_photo_index ?? 0); } })
      .catch(e => { if (!cancelled) setError(e.response?.data?.detail || 'Profile unavailable'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [userId]);

  const retryLoad = () => {
    setLoading(true); setError('');
    getProfileById(userId)
      .then(res => { setProfile(res.data); setPhotoIndex(res.data.primary_photo_index ?? 0); })
      .catch(e => setError(e.response?.data?.detail || 'Profile unavailable'))
      .finally(() => setLoading(false));
  };

  const photos = profile?.photos || [];
  const currentPhoto = photos[photoIndex] || defaultAvatar;

  const nextPhoto = () => photos.length && setPhotoIndex(i => (i + 1) % photos.length);
  const prevPhoto = () => photos.length && setPhotoIndex(i => (i - 1 + photos.length) % photos.length);

  const handleReport = async () => {
    setBusy(true);
    try {
      await reportUser(userId, reportReason, reportDetails);
      setShowReport(false);
      onReported?.();
      onClose();
    } catch (e) {
      alert('Failed to submit report');
    } finally {
      setBusy(false);
    }
  };

  const handleBlock = async () => {
    if (!window.confirm(`Block ${profile?.display_name || profile?.name || 'this member'}? They won't see you and you won't see them.`)) return;
    setBusy(true);
    try {
      await blockUser(userId);
      onBlocked?.();
      onClose();
    } catch (e) {
      alert('Failed to block');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6"
        onClick={onClose}
        data-testid="profile-detail-modal"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 20 }}
          transition={{ type: 'spring', stiffness: 260, damping: 24 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-3xl shadow-2xl w-full max-w-md max-h-[85dvh] overflow-y-auto flex flex-col"
        >
          {/* Header controls */}
          <div className="relative">
            <div className="aspect-[4/5] bg-gray-100 relative">
              {loading ? (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#009E60] border-t-transparent" />
                </div>
              ) : error ? (
                <div className="w-full h-full flex flex-col items-center justify-center text-gray-500 text-sm gap-3 px-6 text-center" data-testid="profile-error">
                  <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center text-2xl">😕</div>
                  <div>{error}</div>
                  <button
                    onClick={retryLoad}
                    className="px-4 py-2 rounded-full bg-[#009E60] text-white text-sm font-semibold hover:bg-[#007A4B]"
                    data-testid="profile-retry-btn"
                  >
                    Try again
                  </button>
                </div>
              ) : (
                <>
                  <img src={currentPhoto} alt={profile?.name} className="w-full h-full object-cover" />
                  {profile?.photo_blurred && (
                    <div className="absolute top-4 left-4 bg-black/60 backdrop-blur text-white text-xs font-medium px-3 py-1.5 rounded-full flex items-center gap-1.5">
                      🔒 <span>Photo protected</span>
                    </div>
                  )}
                  {photos.length > 1 && (
                    <>
                      <button
                        onClick={prevPhoto}
                        className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/70 hover:bg-white flex items-center justify-center"
                        aria-label="Previous photo"
                        data-testid="prev-photo"
                      >
                        <ChevronLeft className="w-5 h-5 text-gray-800" />
                      </button>
                      <button
                        onClick={nextPhoto}
                        className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/70 hover:bg-white flex items-center justify-center"
                        aria-label="Next photo"
                        data-testid="next-photo"
                      >
                        <ChevronRight className="w-5 h-5 text-gray-800" />
                      </button>
                      <div className="absolute top-3 left-1/2 -translate-x-1/2 flex gap-1">
                        {photos.map((_, i) => (
                          <span key={i} className={`h-1 rounded-full transition-all ${i === photoIndex ? 'w-6 bg-white' : 'w-3 bg-white/50'}`} />
                        ))}
                      </div>
                    </>
                  )}
                  <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  <div className="absolute bottom-3 left-4 right-16 text-white">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h2 className="text-2xl font-bold" style={{ fontFamily: 'Outfit' }}>
                        {profile?.display_name || profile?.name}{profile?.age ? `, ${profile.age}` : ''}
                      </h2>
                      {profile?.is_verified && (
                        <span className="inline-flex items-center gap-1 bg-blue-500 text-white text-[10px] font-medium px-2 py-0.5 rounded-full">
                          <Shield className="w-3 h-3" /> Verified
                        </span>
                      )}
                      {profile?.is_online && (
                        <span className="inline-flex items-center gap-1 bg-emerald-500 text-white text-[10px] font-medium px-2 py-0.5 rounded-full">
                          <span className="w-1.5 h-1.5 rounded-full bg-white" /> Online
                        </span>
                      )}
                    </div>
                    {(profile?.current_city || profile?.location) && (
                      <div className="flex items-center gap-1 mt-1 text-white/85 text-sm">
                        <MapPin className="w-4 h-4" />
                        <span>{profile?.current_city || profile?.location}</span>
                      </div>
                    )}
                    {!profile?.is_online && profile?.last_active && (
                      <div className="mt-1"><OnlineDot isOnline={false} lastActive={profile.last_active} size={6} /></div>
                    )}
                  </div>
                </>
              )}
            </div>
            <button
              onClick={onClose}
              className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center backdrop-blur"
              aria-label="Close"
              data-testid="close-profile-modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="p-5 space-y-4">
            {profile?.bio && (
              <p className="text-gray-800 text-sm leading-relaxed">{profile.bio}</p>
            )}

            {profile?.interests?.length > 0 && (
              <div>
                <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Interests</div>
                <div className="flex flex-wrap gap-2">
                  {profile.interests.map((it, i) => (
                    <span key={i} className="px-3 py-1 rounded-full bg-[#009E60]/10 text-[#007A4B] text-xs font-medium">{it}</span>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 text-sm">
              {profile?.looking_for && <Fact label="Looking for" value={profile.looking_for} icon={<Heart className="w-3.5 h-3.5 text-[#009E60]" />} />}
              {profile?.height_cm && <Fact label="Height" value={`${profile.height_cm} cm`} />}
              {profile?.education && <Fact label="Education" value={profile.education} />}
              {profile?.occupation && <Fact label="Work" value={profile.occupation} />}
              {profile?.languages?.length > 0 && <Fact label="Languages" value={profile.languages.join(', ')} />}
              {profile?.zodiac && <Fact label="Zodiac" value={profile.zodiac} />}
              {profile?.ethnicity && <Fact label="Ethnicity" value={profile.ethnicity} />}
              {profile?.religion && <Fact label="Religion" value={profile.religion} />}
              {profile?.smoking && <Fact label="Smoking" value={profile.smoking} />}
              {profile?.drinking && <Fact label="Drinking" value={profile.drinking} />}
              {profile?.has_children && <Fact label="Has children" value={profile.has_children} />}
              {profile?.wants_children && <Fact label="Wants children" value={profile.wants_children} />}
            </div>

            {/* Private Photos */}
            {profile?.private_photos_count > 0 && (
              <div data-testid="private-photos-section">
                <div className="flex items-center gap-2 mb-2">
                  <Lock className="w-4 h-4 text-gray-700" />
                  <div className="text-xs font-semibold text-gray-500 uppercase">
                    Private photos · {profile.private_photos_count}
                  </div>
                </div>
                {profile.private_photos_unlocked && profile.private_photos?.length > 0 ? (
                  <div className="grid grid-cols-3 gap-2">
                    {profile.private_photos.map((url, i) => (
                      <div key={i} className="aspect-square rounded-xl overflow-hidden border border-gray-100">
                        <img src={url} alt={`Private ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-gray-100 rounded-2xl p-4">
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      {[...Array(Math.min(profile.private_photos_count, 3))].map((_, i) => (
                        <div key={i} className="aspect-square rounded-xl bg-gray-300 flex items-center justify-center">
                          <Lock className="w-5 h-5 text-gray-500" />
                        </div>
                      ))}
                    </div>
                    {profile.private_photo_request_pending ? (
                      <div className="text-center text-sm text-gray-600" data-testid="private-request-pending">
                        Request sent. We'll notify you when they respond.
                      </div>
                    ) : (
                      <Button
                        onClick={() => setShowPrivateRequest(true)}
                        className="w-full h-10 rounded-full bg-gray-800 hover:bg-black text-white text-sm font-semibold"
                        data-testid="request-private-photos-btn"
                      >
                        <Lock className="w-4 h-4 mr-2" /> Request access
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}

            {profile?.prompts?.length > 0 && (
              <div className="space-y-2">
                <div className="text-xs font-semibold text-gray-500 uppercase">Prompts</div>
                {profile.prompts.map((p, i) => (
                  <div key={i} className="bg-gray-50 rounded-xl p-3">
                    <div className="text-xs text-gray-500 mb-1">{p.question}</div>
                    <div className="text-sm text-gray-800">{p.answer}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Safety actions */}
            <div className="pt-3 border-t border-gray-100 flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowReport(true)}
                className="text-red-600 hover:bg-red-50 flex-1"
                data-testid="modal-report"
              >
                <Flag className="w-4 h-4 mr-1" /> Report
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBlock}
                disabled={busy}
                className="text-gray-700 hover:bg-gray-100 flex-1"
                data-testid="modal-block"
              >
                <Ban className="w-4 h-4 mr-1" /> Block
              </Button>
            </div>
          </div>

          {/* Bottom action row — always visible so Message stays reachable even while profile loads or fails */}
          <div className="sticky bottom-0 border-t border-gray-100 p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] flex items-center gap-2 bg-white">
            <Button
              onClick={() => profile && onMessage?.(profile)}
              disabled={!profile || loading}
              className="flex-1 bg-[#009E60] hover:bg-[#007A4B] rounded-full h-11 disabled:opacity-60"
              data-testid="modal-message-btn"
            >
              <MessageCircle className="w-4 h-4 mr-2" /> Message
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowGifts(true)}
              disabled={!profile}
              className="rounded-full h-11 px-4 border-pink-500 text-pink-500 hover:bg-pink-50 disabled:opacity-60"
              data-testid="modal-gift-btn"
            >
              <Gift className="w-4 h-4 mr-1" /> Gift
            </Button>
            {onLike && (
              <Button
                variant="outline"
                onClick={() => { if (profile) { onLike(profile); onClose(); } }}
                disabled={!profile}
                className="rounded-full h-11 px-4 border-[#009E60] text-[#009E60] hover:bg-[#009E60]/5 disabled:opacity-60"
                data-testid="modal-like-btn"
              >
                <Heart className="w-4 h-4" />
              </Button>
            )}
          </div>

          {giftSent && (
            <div className="mx-4 mb-3 bg-pink-50 text-pink-700 rounded-xl p-2 text-sm text-center" data-testid="gift-sent-toast">
              Sent {giftSent.emoji} {giftSent.name}!
            </div>
          )}

          {showGifts && (
            <GiftPickerModal
              recipientId={userId}
              recipientName={profile?.display_name || profile?.name}
              userCredits={(user?.credits || 0) + (user?.promo_credits || 0)}
              onClose={() => setShowGifts(false)}
              onSent={(g) => { setGiftSent(g); setTimeout(() => setGiftSent(null), 3000); }}
            />
          )}

          {/* Private-photo request sheet */}
          <AnimatePresence>
            {showPrivateRequest && (
              <motion.div
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{ type: 'spring', damping: 30, stiffness: 250 }}
                className="absolute inset-x-0 bottom-0 bg-white rounded-t-3xl border-t border-gray-100 p-4 z-[110]"
                data-testid="private-request-sheet"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Lock className="w-4 h-4 text-gray-700" />
                    <h3 className="font-semibold text-gray-900">Request private photos</h3>
                  </div>
                  <button onClick={() => setShowPrivateRequest(false)} className="p-1 rounded-full hover:bg-gray-100" data-testid="private-request-close">
                    <X className="w-4 h-4 text-gray-500" />
                  </button>
                </div>
                <p className="text-xs text-gray-500 mb-3">
                  {profile?.display_name || profile?.name} will get a notification and can approve or decline. You'll be notified when they respond.
                </p>
                <Textarea
                  value={privateNote}
                  onChange={(e) => setPrivateNote(e.target.value.slice(0, 280))}
                  placeholder="Optional message"
                  className="min-h-[70px] rounded-xl resize-none"
                  data-testid="private-request-note"
                />
                <p className="text-xs text-gray-400 text-right mt-1">{privateNote.length}/280</p>
                {privateStatus === 'error' && (
                  <div className="bg-red-50 text-red-600 rounded-xl p-2 text-sm mt-2">Could not send request</div>
                )}
                <div className="flex gap-2 mt-3">
                  <Button variant="ghost" onClick={() => setShowPrivateRequest(false)} className="flex-1" data-testid="private-request-cancel">Cancel</Button>
                  <Button
                    onClick={async () => {
                      setPrivateStatus('pending');
                      try {
                        await requestPrivatePhotos(userId, privateNote.trim());
                        setPrivateStatus('sent');
                        setShowPrivateRequest(false);
                        // Optimistically mark pending in local profile state
                        setProfile((p) => p ? { ...p, private_photo_request_pending: true } : p);
                      } catch {
                        setPrivateStatus('error');
                      }
                    }}
                    disabled={privateStatus === 'pending'}
                    className="flex-1 bg-gray-800 hover:bg-black rounded-full"
                    data-testid="private-request-send"
                  >
                    <Send className="w-4 h-4 mr-1" /> {privateStatus === 'pending' ? 'Sending…' : 'Send request'}
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Report sheet */}
          <AnimatePresence>
            {showReport && (
              <motion.div
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{ type: 'spring', stiffness: 260, damping: 26 }}
                className="absolute inset-x-0 bottom-0 bg-white rounded-t-3xl p-5 shadow-2xl border-t border-gray-100"
                data-testid="report-sheet"
              >
                <div className="w-10 h-1 rounded-full bg-gray-200 mx-auto mb-3" />
                <h3 className="font-semibold text-gray-900 mb-3">Report member</h3>
                <select
                  value={reportReason}
                  onChange={(e) => setReportReason(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 mb-3"
                  data-testid="report-reason"
                >
                  <option value="inappropriate">Inappropriate photos or content</option>
                  <option value="fake">Fake profile / impersonation</option>
                  <option value="spam">Spam or scam</option>
                  <option value="harassment">Harassment or abuse</option>
                  <option value="other">Other</option>
                </select>
                <textarea
                  value={reportDetails}
                  onChange={(e) => setReportDetails(e.target.value)}
                  placeholder="Optional details…"
                  rows={3}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 mb-3 text-sm"
                  data-testid="report-details"
                />
                <div className="flex gap-2">
                  <Button variant="ghost" onClick={() => setShowReport(false)} className="flex-1">Cancel</Button>
                  <Button onClick={handleReport} disabled={busy} className="flex-1 bg-red-500 hover:bg-red-600" data-testid="report-submit">
                    {busy ? 'Sending…' : 'Submit report'}
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const Fact = ({ label, value, icon }) => (
  <div className="bg-gray-50 rounded-xl px-3 py-2">
    <div className="text-[10px] text-gray-500 uppercase tracking-wide flex items-center gap-1">{icon}{label}</div>
    <div className="text-sm text-gray-900 mt-0.5 capitalize">{value}</div>
  </div>
);

export default ProfileDetailModal;
